import { useState } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Button, PageHeader, Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components';
import { PluginProblem } from './PluginProblem';
import { pluginCapabilityLabel, pluginStateLabel } from './pluginPresentation';
import { usePluginCatalogue } from './usePluginCatalogue';
import { PluginSetupDialog } from './PluginSetupDialog';
import { PluginLifecycleDialog } from './PluginLifecycleDialog';
import { PluginTeam } from './PluginTeam';
import { PluginAutomationSettingsLink } from './PluginAutomationSettingsLink';
import { PluginConnectionTab } from './PluginConnectionTab';

export function PluginDetailPage() {
  const { pluginId } = useParams({ from: '/_admin/plugins/$pluginId' });
  const { tab } = useSearch({ from: '/_admin/plugins/$pluginId' });
  const navigate = useNavigate();
  const query = usePluginCatalogue();
  const [setup, setSetup] = useState(false);
  const [action, setAction] = useState<'disable' | 'revoke' | null>(null);
  const plugin = query.data?.find((x) => x.id === pluginId);
  if (!plugin)
    return (
      <div className="space-y-4">
        <PluginProblem error={query.error} />
        <p>
          {query.isPending
            ? 'Plugin wird geladen…'
            : query.isError
              ? 'Der Katalog ist nicht erreichbar.'
              : 'Dieses Plugin ist nicht im Katalog.'}
        </p>
        <Button size="lg" onClick={() => void query.refetch()}>
          Erneut laden
        </Button>
      </div>
    );
  const installed = plugin.installation && plugin.installation.state !== 'Revoked';
  const state = plugin.installation?.state;
  return (
    <>
      <Link to="/plugins" className="text-ink-soft mb-3 inline-flex min-h-11 items-center text-sm">
        ← Alle Plugins
      </Link>
      <PageHeader
        eyebrow="Plugin"
        title={plugin.name}
        description="Apartmentwissen und Gästekommunikation in einem eigenen Arbeitsbereich."
      />
      <PluginProblem error={query.error} />
      <div className="bg-card border-line/60 shadow-card rounded-lg border p-4 sm:p-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <span className="bg-sage/10 text-ink rounded-full px-4 py-2 text-sm">
            {pluginStateLabel(state)}
          </span>
          {plugin.canManage && (!installed || state === 'Disabled') && plugin.entitled && (
            <Button type="button" variant="primary" size="lg" onClick={() => setSetup(true)}>
              {state === 'Disabled' ? 'Erneut aktivieren' : 'Einrichten'}
            </Button>
          )}
        </div>
        <Tabs
          defaultValue="overview"
          value={tab}
          onValueChange={(next) =>
            void navigate({ to: '/plugins/$pluginId', params: { pluginId }, search: { tab: next } })
          }
        >
          <TabsList className="overflow-x-auto">
            <TabsTrigger value="overview" className="shrink-0">
              Übersicht
            </TabsTrigger>
            <TabsTrigger value="team" className="shrink-0">
              Team & Rechte
            </TabsTrigger>
            <TabsTrigger value="connection" className="shrink-0">
              Verbindung
            </TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="space-y-6">
            <section>
              <h2 className="font-serif text-xl">Ihr Nutzungsrecht</h2>
              <p className="text-ink-soft mt-2">
                {!plugin.entitled
                  ? 'Für diesen Workspace liegt keine Freigabe vor.'
                  : {
                      Free: 'Kostenfrei nutzbar.',
                      Included: 'In Ihrem Paket enthalten.',
                      AdminGranted: 'Für Ihren Workspace freigegeben.',
                    }[plugin.entitlement]}
              </p>
            </section>
            <section>
              <h2 className="font-serif text-xl">Freigegebener Umfang</h2>
              <p className="text-ink-soft mt-2">
                {installed
                  ? 'Alle aktuellen und künftigen Apartments dieses Workspaces.'
                  : 'Noch keine Datenfreigabe.'}
              </p>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm">
                {(installed ? plugin.installation!.capabilities : []).map((cap) => (
                  <li key={cap}>{pluginCapabilityLabel(cap)}</li>
                ))}
              </ul>
              <p className="text-ink-soft mt-3 text-sm">
                Personenbezogene Gästekommunikation:{' '}
                {plugin.installation?.guestCommunicationPii
                  ? 'im Installationsumfang enthalten; persönliche Rechte gelten zusätzlich'
                  : 'nicht freigegeben'}
                .
              </p>
            </section>
            <PluginAutomationSettingsLink plugin={plugin} />
          </TabsContent>
          <TabsContent value="team">
            <PluginTeam plugin={plugin} />
          </TabsContent>
          <TabsContent value="connection" className="space-y-6">
            <PluginConnectionTab plugin={plugin} onAction={setAction} />
          </TabsContent>
        </Tabs>
      </div>
      {setup && <PluginSetupDialog plugin={plugin} onClose={() => setSetup(false)} />}
      {action && plugin.installation && (
        <PluginLifecycleDialog
          installation={plugin.installation}
          action={action}
          onClose={() => setAction(null)}
        />
      )}
    </>
  );
}
